import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const WELCOME_MESSAGES = {
  en: "Hi! 🐰 I'm Mr. Bunny, Aryan's AI assistant! Ask me anything about his services, portfolio, or digital marketing expertise!",
  de: "Hallo! 🐰 Ich bin Mr. Bunny, Aryans KI-Assistent! Frag mich alles über seine Dienstleistungen, Portfolio oder Digital-Marketing-Expertise!",
  es: "¡Hola! 🐰 Soy Mr. Bunny, el asistente de IA de Aryan. ¡Pregúntame sobre sus servicios, portafolio o experiencia en marketing digital!",
  fr: "Salut! 🐰 Je suis Mr. Bunny, l'assistant IA d'Aryan! Demandez-moi n'importe quoi sur ses services, portfolio ou expertise en marketing digital!",
  it: "Ciao! 🐰 Sono Mr. Bunny, l'assistente AI di Aryan! Chiedimi qualsiasi cosa sui suoi servizi, portfolio o competenze di marketing digitale!",
  pt: "Olá! 🐰 Sou Mr. Bunny, o assistente de IA do Aryan! Pergunte-me sobre seus serviços, portfólio ou expertise em marketing digital!",
  hi: "नमस्ते! 🐰 मैं Mr. Bunny हूं, अर्यन का AI सहायक! उनकी सेवाओं, पोर्टफोलियो या डिजिटल मार्केटिंग विशेषज्ञता के बारे में मुझसे कुछ भी पूछें!",
  ar: "مرحبا! 🐰 أنا Mr. Bunny، مساعد الذكاء الاصطناعي لأريان! اسألني عن خدماته أو محفظته أو خبرته في التسويق الرقمي!",
  zh: "你好！🐰 我是 Mr. Bunny，Aryan 的 AI 助手！问我关于他的服务、作品集或数字营销专业知识的任何问题！",
  ja: "こんにちは！🐰 私はMr. Bunnyです、AryanのAIアシスタント！彼のサービス、ポートフォリオ、デジタルマーケティングの専門知識について何でも聞いてください！"
};

const getWelcomeMessage = (lang) => WELCOME_MESSAGES[lang] || WELCOME_MESSAGES.en;

const MrBunnyChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const { i18n } = useTranslation();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: getWelcomeMessage(i18n.language)
        }
      ]);
    }
  }, [isOpen, messages.length, i18n.language]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage;
    setInputMessage('');
    
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          language: i18n.language
        })
      });

      const data = await response.json();
      
      // Add Mr. Bunny's response
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Oops! 🐰 I'm having trouble connecting. Please try again!" 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-16 h-16 bg-gold rounded-full shadow-2xl hover:scale-110 transition-transform z-50 flex items-center justify-center group"
          aria-label="Chat with Mr. Bunny"
        >
          <span className="text-3xl">🐰</span>
          <div className="absolute -top-2 -left-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
            AI
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-black border-2 border-gold rounded-2xl shadow-2xl z-50 flex flex-col">
          {/* Header */}
          <div className="bg-gold text-black px-4 py-3 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🐰</span>
              <div>
                <h3 className="font-sans font-bold text-sm">Mr. Bunny</h3>
                <p className="text-xs opacity-80">AI Assistant</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-black hover:opacity-60 transition-opacity"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-black/95">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-gold text-black'
                      : 'bg-gray-800 text-white border border-gold/30'
                  }`}
                >
                  {msg.role === 'assistant' && <span className="mr-1">🐰</span>}
                  <span className="text-sm whitespace-pre-wrap">{msg.content}</span>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-800 text-white border border-gold/30 px-4 py-2 rounded-lg">
                  <span className="mr-1">🐰</span>
                  <span className="text-sm">Thinking...</span>
                  <span className="ml-1 animate-pulse">✨</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gold/30 bg-black rounded-b-2xl">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything..."
                className="flex-1 px-4 py-2 bg-gray-900 text-white border border-gold/30 rounded-lg focus:outline-none focus:border-gold text-sm"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputMessage.trim()}
                className="px-4 py-2 bg-gold text-black rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MrBunnyChatbot;
